import React, { useState, useEffect } from 'react';
import api, { openDocument } from '../api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const CATEGORIES = [
  { value: 'handbook', label: 'Company Handbook' },
  { value: 'policy', label: 'Policy' },
  { value: 'form', label: 'Form' },
  { value: 'other', label: 'Other' },
];

const CompanyDocuments = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ title: '', category: 'handbook', requires_signature: false, file: null });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/documents/company');
      setDocs(r.data);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.title) return setError('Please give the document a title');
    if (!form.file) return setError('Please choose a file');
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('category', form.category);
      fd.append('requires_signature', form.requires_signature);
      await api.post('/api/documents/company', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Document uploaded');
      setForm({ title: '', category: 'handbook', requires_signature: false, file: null });
      e.target.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document for everyone?')) return;
    try { await api.delete(`/api/documents/company/${id}`); load(); }
    catch (err) { setError('Failed to delete'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Documents</h1>
          <p className="page-subtitle">Handbooks, policies and forms shared with the whole team.</p>
        </div>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      {isAdmin && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header"><h3 className="card-title">Upload a document</h3></div>
          <div className="card-body">
            <form onSubmit={handleUpload}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label className="form-label">Title</label>
                  <input className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Employee Handbook 2026" />
                </div>
                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label className="form-label">Category</label>
                  <select className="form-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label className="form-label">File</label>
                  <input type="file" className="form-control" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={e => setForm(f => ({ ...f, file: e.target.files[0] }))} />
                </div>
                <div className="form-group" style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: 'var(--navy)' }}>
                    <input type="checkbox" checked={form.requires_signature} onChange={e => setForm(f => ({ ...f, requires_signature: e.target.checked }))} style={{ accentColor: 'var(--red)' }} />
                    Require staff to acknowledge receipt
                  </label>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Uploading...' : 'Upload'}</button>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header"><h3 className="card-title">Documents</h3></div>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : docs.length === 0 ? (
          <div className="empty-state"><h3>No documents yet</h3><p>{isAdmin ? 'Upload your handbook and policies above.' : 'Nothing has been shared yet.'}</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Title</th><th>Category</th><th>Added</th><th>Acknowledgement</th><th></th></tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.title}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(d.category || '').replace('_', ' ')}</td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{format(new Date(d.created_at), 'd MMM yyyy')}</td>
                    <td>
                      {d.requires_signature
                        ? (d.signed ? <span className="badge badge-approved">Acknowledged</span> : <span className="badge badge-pending">Required</span>)
                        : <span style={{ color: 'var(--grey-mid)', fontSize: '12px' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button>
                      {isAdmin && <> <button className="btn btn-danger btn-sm" onClick={() => remove(d.id)}>Delete</button></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDocuments;
